import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Pencil, Trash2, Check } from "lucide-react";
import { apiClient } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stockEdits, setStockEdits] = useState({});
  const [costEdits, setCostEdits] = useState({});
  const [savingId, setSavingId] = useState(null);

  const fetchProducts = useCallback(() => {
    apiClient.get("/admin/products").then((res) => setProducts(res.data.items)).finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await apiClient.delete(`/admin/products/${id}`);
    toast.success("Product deleted");
    fetchProducts();
  };

  const saveStock = async (p) => {
    const value = parseInt(stockEdits[p.id], 10);
    if (isNaN(value) || value < 0) return toast.error("Enter a valid stock quantity");
    setSavingId(p.id);
    try {
      await apiClient.put(`/admin/products/${p.id}`, { stock_quantity: value });
      toast.success(`${p.name} stock updated to ${value}`);
      setStockEdits((s) => { const n = { ...s }; delete n[p.id]; return n; });
      fetchProducts();
    } catch {
      toast.error("Could not update stock");
    } finally {
      setSavingId(null);
    }
  };

  const saveCost = async (p) => {
    const raw = costEdits[p.id];
    const value = raw === "" ? null : parseFloat(raw);
    if (value !== null && (isNaN(value) || value < 0)) return toast.error("Enter a valid unit cost");
    setSavingId(p.id);
    try {
      await apiClient.put(`/admin/products/${p.id}`, { unit_cost: value });
      toast.success(`${p.name} cost updated`);
      setCostEdits((s) => { const n = { ...s }; delete n[p.id]; return n; });
      fetchProducts();
    } catch {
      toast.error("Could not update cost");
    } finally {
      setSavingId(null);
    }
  };

  const marginFor = (p) => {
    if (p.unit_cost == null || !p.price) return null;
    const profit = p.price - p.unit_cost;
    return { profit, pct: (profit / p.price) * 100 };
  };

  return (
    <div data-testid="admin-products-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-display font-semibold text-slate-900">Products</h1>
        <Link to="/admin/products/new"><Button size="sm" className="rounded-md" data-testid="admin-new-product-btn"><Plus className="h-4 w-4 mr-1.5" /> New Product</Button></Link>
      </div>

      <div className="bg-white border border-slate-200 rounded-md shadow-sm overflow-x-auto">
        <table className="w-full text-sm text-slate-600">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">SKU</th>
              <th className="px-4 py-3">Price</th>
              <th className="px-4 py-3">Unit Cost</th>
              <th className="px-4 py-3">Margin</th>
              <th className="px-4 py-3">Stock</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Featured</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {!loading && products.map((p) => (
              <tr key={p.id} className="border-t border-slate-100" data-testid={`admin-product-row-${p.slug}`}>
                <td className="px-4 py-3 font-medium text-slate-900">{p.name}</td>
                <td className="px-4 py-3">{p.sku}</td>
                <td className="px-4 py-3">${p.price.toFixed(2)}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1.5">
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="—"
                      className="w-24 h-8 text-sm"
                      value={costEdits[p.id] !== undefined ? costEdits[p.id] : (p.unit_cost != null ? p.unit_cost : "")}
                      onChange={(e) => setCostEdits((s) => ({ ...s, [p.id]: e.target.value }))}
                      data-testid={`admin-product-cost-${p.slug}`}
                    />
                    {costEdits[p.id] !== undefined && String(costEdits[p.id]) !== String(p.unit_cost != null ? p.unit_cost : "") && (
                      <Button size="sm" className="h-8 px-2 rounded-md" disabled={savingId === p.id} onClick={() => saveCost(p)} data-testid={`admin-save-cost-${p.slug}`}>
                        <Check className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3" data-testid={`admin-product-margin-${p.slug}`}>
                  {(() => {
                    const m = marginFor(p);
                    if (!m) return <span className="text-slate-300">—</span>;
                    return (
                      <span className={m.profit >= 0 ? "text-emerald-600 font-semibold" : "text-red-600 font-semibold"}>
                        ${m.profit.toFixed(2)} <span className="text-[10px] font-normal">({m.pct.toFixed(0)}%)</span>
                      </span>
                    );
                  })()}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1.5">
                    <Input
                      type="number"
                      min="0"
                      className={`w-20 h-8 text-sm ${p.stock_quantity === 0 ? "text-red-600 font-semibold" : p.stock_quantity <= 5 ? "text-amber-600 font-semibold" : ""}`}
                      value={stockEdits[p.id] !== undefined ? stockEdits[p.id] : p.stock_quantity}
                      onChange={(e) => setStockEdits((s) => ({ ...s, [p.id]: e.target.value }))}
                      data-testid={`admin-product-stock-${p.slug}`}
                    />
                    {stockEdits[p.id] !== undefined && String(stockEdits[p.id]) !== String(p.stock_quantity) && (
                      <Button size="sm" className="h-8 px-2 rounded-md" disabled={savingId === p.id} onClick={() => saveStock(p)} data-testid={`admin-save-stock-${p.slug}`}>
                        <Check className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    {p.stock_quantity === 0 ? <span className="text-[10px] text-red-600 font-semibold uppercase">Out</span> : p.stock_quantity <= 5 ? <span className="text-[10px] text-amber-600 font-semibold uppercase">Low</span> : null}
                  </div>
                </td>
                <td className="px-4 py-3 capitalize">{p.status}</td>
                <td className="px-4 py-3">{p.featured ? "Yes" : "No"}</td>
                <td className="px-4 py-3 flex gap-2 justify-end">
                  <Link to={`/admin/products/${p.id}/edit`}><Button size="sm" variant="outline" data-testid={`admin-edit-product-${p.slug}`}><Pencil className="h-3.5 w-3.5" /></Button></Link>
                  <Button size="sm" variant="outline" onClick={() => handleDelete(p.id)} data-testid={`admin-delete-product-${p.slug}`}><Trash2 className="h-3.5 w-3.5 text-red-500" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!loading && products.length === 0 && <p className="p-6 text-center text-slate-400 text-sm" data-testid="admin-no-products">No products yet.</p>}
      </div>
    </div>
  );
}
